import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Phone, MessageCircle, AlertTriangle, Flame } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import FloatingWhatsApp from './FloatingWhatsApp';

const BlogCaldaiaRottaCosaFare = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <Helmet>
        <title>Caldaia Rotta Cosa Fare a Roma - Guida Urgente | SOS Casa Roma</title>
        <meta name="description" content="Caldaia rotta a Roma? Scopri cosa fare immediatamente. Chiamaci H24 per intervento urgente entro 60 minuti. Tel: 328 768 6356" />
        <link rel="canonical" href={`${window.location.origin}/blog/caldaia-rotta-cosa-fare`} />
      </Helmet>
      
      <div className="min-h-screen">
        <Header />
        
        <article className="py-12 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="bg-red-100 border-l-4 border-red-600 p-4 mb-6">
              <div className="flex items-center">
                <Flame className="text-red-600 mr-2" />
                <p className="font-bold text-red-800">CALDAIA SPENTA? Chiamaci subito: 328 768 6356</p>
              </div>
            </div>

            <h1 className="text-4xl font-bold mb-6 text-gray-900">
              Caldaia Rotta a Roma: Cosa Fare Immediatamente
            </h1>
            
            <div className="prose max-w-none">
              <p className="text-lg text-gray-700 mb-6">
                La tua <strong>caldaia è in blocco</strong> e non hai acqua calda? Niente panico. 
                Segui questi passi:
              </p>

              <div className="bg-blue-50 rounded-lg p-6 mb-6">
                <h2 className="text-2xl font-bold mb-4 text-gray-900">1️⃣ Controlla il Display della Caldaia</h2>
                <p className="text-gray-700 mb-3">
                  La caldaia mostra un <strong>codice errore</strong>? Annotalo (es: E01, F28, A03).
                </p>
                <p className="text-sm text-gray-600">Questo aiuta il tecnico a diagnosticare il problema più velocemente.</p>
              </div>

              <div className="bg-orange-50 rounded-lg p-6 mb-6">
                <h2 className="text-2xl font-bold mb-4 text-gray-900">2️⃣ Verifica la Pressione</h2>
                <p className="text-gray-700 mb-3">
                  Guarda il manometro (l'indicatore rotondo). La pressione deve essere tra <strong>1 e 2 bar</strong>.
                </p>
                <ul className="list-disc pl-6 text-gray-700">
                  <li className="mb-2"><strong>Sotto 1 bar?</strong> Aggiungi acqua dal rubinetto sotto la caldaia</li>
                  <li><strong>Sopra 2.5 bar?</strong> Chiama subito un tecnico</li>
                </ul>
              </div>

              <div className="bg-red-50 rounded-lg p-6 mb-6">
                <h2 className="text-2xl font-bold mb-4 text-gray-900">3️⃣ NON Tentare Riparazioni Fai-da-Te</h2>
                <p className="text-gray-700 mb-4">
                  Le caldaie sono <strong>apparecchi a gas</strong>. Riparazioni fai-da-te possono essere pericolose.
                </p>
                <div className="bg-white rounded-lg p-4 border-2 border-red-500">
                  <p className="font-bold text-red-800 mb-2">🔥 SOS Casa Roma - Riparazione Caldaie H24</p>
                  <p className="text-gray-700 mb-2">✅ Tecnici certificati per tutte le marche</p>
                  <p className="text-gray-700 mb-4">✅ Arriviamo entro 60 minuti a Roma</p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a href="tel:3287686356" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-bold flex items-center gap-2 justify-center break-words">
                      <Phone className="mr-2 h-5 w-5" />
                      CHIAMA ORA: 328 768 6356
                    </a>
                    <a href="https://wa.me/393287686356" target="_blank" rel="noopener noreferrer" className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-bold flex items-center gap-2 justify-center break-words">
                      <MessageCircle className="mr-2 h-5 w-5" />
                      SCRIVI SU WHATSAPP
                    </a>
                  </div>
                </div>
              </div>

              <h3 className="text-xl font-bold mb-3 text-gray-900">Problemi Caldaia Più Comuni a Roma:</h3>
              <ul className="list-disc pl-6 mb-6 text-gray-700">
                <li className="mb-2"><strong>Caldaia in blocco:</strong> Non si accende, display spento o lampeggiante</li>
                <li className="mb-2"><strong>Niente acqua calda:</strong> Rubinetti escono freddi ma riscaldamento funziona</li>
                <li className="mb-2"><strong>Pressione bassa:</strong> Caldaia si spegne continuamente</li>
                <li className="mb-2"><strong>Rumori strani:</strong> Fischi, borbottii, colpi metallici</li>
                <li className="mb-2"><strong>Perdita acqua dalla caldaia:</strong> URGENTE - Chiama subito</li>
              </ul>

              <div className="bg-yellow-50 border-2 border-yellow-500 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-bold mb-3 text-gray-900">⚠️ Quando è URGENTE chiamare:</h3>
                <ul className="list-disc pl-6 text-gray-700">
                  <li>Odore di gas vicino alla caldaia</li>
                  <li>Perdita d'acqua dalla caldaia</li>
                  <li>Caldaia fa rumore molto forte</li>
                  <li>Fumo o bruciature vicino alla caldaia</li>
                </ul>
                <p className="mt-4 font-bold text-red-700">In questi casi SPEGNI LA CALDAIA e chiamaci immediatamente.</p>
              </div>

              <h3 className="text-xl font-bold mb-3 text-gray-900">Quanto Costa Riparare una Caldaia a Roma?</h3>
              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <p className="text-gray-700 mb-2">• <strong>Uscita tecnico:</strong> 40€</p>
                <p className="text-gray-700 mb-2">• <strong>Intervento entro 1 ora:</strong> 60€</p>
                <p className="text-gray-700 mb-2">• <strong>Emergenza notturna:</strong> da 80€</p>
                <p className="text-sm text-gray-600 mt-2">Riparazione inclusa nel costo • Pezzi di ricambio a parte</p>
              </div>

              <h3 className="text-xl font-bold mb-3 text-gray-900">Conviene Riparare o Sostituire?</h3>
              <p className="text-gray-700 mb-4">
                Se la tua caldaia ha <strong>più di 15 anni</strong> e si rompe spesso, potrebbe convenire sostituirla con una nuova a condensazione (risparmio fino al 30% in bolletta).
              </p>
              <p className="text-gray-700 mb-6">
                Il nostro tecnico ti consiglierà la soluzione migliore <strong>senza obblighi</strong>.
              </p>

              <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6 text-center">
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Caldaia Rotta Adesso?</h3>
                <p className="text-lg text-gray-700 mb-4">Non restare al freddo. Chiamaci subito.</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a href="tel:3287686356" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-bold flex items-center gap-2 justify-center break-words">
                      <Phone className="mr-2 h-6 w-6" />
                      CHIAMA ORA: 328 768 6356
                    </a>
                  <a href="https://wa.me/393287686356" target="_blank" rel="noopener noreferrer" className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-bold flex items-center gap-2 justify-center break-words">
                      <MessageCircle className="mr-2 h-6 w-6" />
                      WHATSAPP IMMEDIATO
                    </a>
                </div>
                <p className="text-sm text-gray-600 mt-4">✅ Intervento H24 • ✅ Tutte le marche • ✅ Prezzi chiari</p>
              </div>
            </div>
          </div>
        </article>
        
        <Footer />
        <FloatingWhatsApp />
      </div>
    </>
  );
};

export default BlogCaldaiaRottaCosaFare;